/**
 * InputControlManager - Coordinator-level API for input configuration.
 *
 * Encapsulates pause state management, gamepad slot delegation, stats access,
 * and AFK tracking. PeerCoordinator delegates to this manager to keep its
 * own file size within limits.
 *
 * Responsibilities:
 * - Pause: global and per-peer pause via InputConfigStores
 * - Gamepad: slot assignment/removal via GamepadManager
 * - Stats: pass-through to PeerStatsManager
 * - AFK: pass-through to ActivityTracker
 */

import type { PlaycastGamepadId } from '@playcastdotio/CoreTypes';
import type { CoordinatorEvent } from '../../_types/events';
import type { InputConfigStores, GamepadConfigState, PauseInputState } from '../stores/inputConfigStores';
import type { GamepadManager } from './GamepadManager';
import type { PeerStatsManager, PeerStats } from './PeerStatsManager';
import type { ActivityTracker } from './ActivityTracker';

export type InputControlEvents = {
  pauseStateChanged: CoordinatorEvent<{ peerId?: string; paused: boolean; global: boolean }>;
  gamepadSlotsChanged: CoordinatorEvent<{ peerId: string; slots: PlaycastGamepadId[] }>;
  afkStateChanged: CoordinatorEvent<{ peerId: string; afk: boolean }>;
};

export class InputControlManager {
  private inputStores: InputConfigStores | null = null;
  private gamepadManager: GamepadManager | null = null;
  private activityTracker: ActivityTracker | null = null;

  constructor(
    private readonly statsManager: PeerStatsManager,
    private readonly events: InputControlEvents,
  ) {}

  // ========================================
  // Registration (called from init.ts)
  // ========================================

  public registerInputStores(stores: InputConfigStores): void {
    this.inputStores = stores;
  }

  public registerGamepadManager(manager: GamepadManager): void {
    this.gamepadManager = manager;
  }

  public registerActivityTracker(tracker: ActivityTracker): void {
    this.activityTracker = tracker;
  }

  // ========================================
  // Pause API
  // ========================================

  public setPeerPaused(peerId: string, paused: boolean): void {
    if (!this.inputStores) return;
    const store = this.inputStores.pauseInput;
    const current = store.currentState;

    store.updateState({
      ...current,
      players: { ...current.players, [peerId]: { paused } },
    });

    this.events.pauseStateChanged.emit({ peerId, paused, global: false });
  }

  public setGlobalPaused(paused: boolean): void {
    if (!this.inputStores) return;
    const store = this.inputStores.pauseInput;

    store.updateState({ ...store.currentState, paused });
    this.events.pauseStateChanged.emit({ peerId: undefined, paused, global: true });
  }

  public isPeerPaused(peerId: string): boolean {
    if (!this.inputStores) return false;
    return !!this.inputStores.pauseInput.currentState.players[peerId]?.paused;
  }

  public isGloballyPaused(): boolean {
    if (!this.inputStores) return false;
    return this.inputStores.pauseInput.currentState.paused;
  }

  public getPauseState(): PauseInputState {
    if (!this.inputStores) return { paused: false, players: {} };
    return this.inputStores.pauseInput.currentState;
  }

  // ========================================
  // Gamepad Slot API
  // ========================================

  public assignGamepadSlot(peerId: string, slotIndex: number): void {
    if (!this.gamepadManager) return;
    this.gamepadManager.assignSlot(peerId, slotIndex as PlaycastGamepadId);
    this.emitGamepadSlotsChanged(peerId);
  }

  public removeGamepadSlot(peerId: string, slotIndex: number): void {
    if (!this.gamepadManager) return;
    this.gamepadManager.removeSlot(peerId, slotIndex as PlaycastGamepadId);
    this.emitGamepadSlotsChanged(peerId);
  }

  public getGamepadSlots(peerId: string): PlaycastGamepadId[] {
    if (!this.gamepadManager) return [];
    return this.gamepadManager.getSlotAssignments(peerId);
  }

  public getGamepadConfig(): GamepadConfigState {
    if (!this.inputStores) {
      return { autoAssign: true, maxAssignments: 4, shareFlags: [false, false, false, false] };
    }
    return this.inputStores.gamepadConfig.currentState;
  }

  public updateGamepadConfig(config: Partial<GamepadConfigState>): void {
    if (!this.inputStores) return;
    const store = this.inputStores.gamepadConfig;
    store.updateState({ ...store.currentState, ...config });
  }

  // ========================================
  // Stats API (poll-based)
  // ========================================

  public getPeerStats(peerId: string): PeerStats | undefined {
    return this.statsManager.getLatestStats(peerId);
  }

  public getAllPeerStats(): PeerStats[] {
    return this.statsManager.getAllStats();
  }

  // ========================================
  // Activity / AFK API
  // ========================================

  public isPlayerAfk(peerId: string): boolean {
    if (!this.activityTracker) return false;
    return this.activityTracker.isAfk(peerId);
  }

  public getActivityState(): Record<string, { afk: boolean; lastActivityAt: number }> {
    if (!this.activityTracker) return {};
    return this.activityTracker.getActivityState();
  }

  // ========================================
  // Internal
  // ========================================

  private emitGamepadSlotsChanged(peerId: string): void {
    const slots = this.getGamepadSlots(peerId);
    this.events.gamepadSlotsChanged.emit({ peerId, slots });
  }
}
